import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, getImageUrl } from '../api';
import Footer from '../components/Footer';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Search, MapPin, ArrowRight, Loader, Tag } from 'react-feather';

// --- COMPONENTE: TARJETA DE EVENTO ---
const EventCard = ({ event, index }) => {
  const navigate = useNavigate();
  const dateObj = parseISO(event.date);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -10 }}
      onClick={() => navigate(`/event/${event.id}`)}
      className="group relative w-full aspect-[3/4] rounded-3xl overflow-hidden cursor-pointer bg-[#111] border border-white/10 hover:border-white/30 transition-colors shadow-2xl"
    >
      {/* IMAGEN DE FONDO con Zoom suave */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src={getImageUrl(event.image_url) || 'https://placehold.co/800x1200/1a1a1a/white?text=No+Image'}
          alt={event.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-80 group-hover:opacity-100"
        />
      </div>

      {/* GRADIENTES PARA LEGIBILIDAD */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />
      
      {/* CONTENIDO SUPERIOR (FECHA BADGE) */}
      <div className="absolute top-4 right-4 z-20">
        <div className="flex flex-col items-center justify-center bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-3 min-w-[60px] text-center">
          <span className="text-xs font-bold uppercase text-white/80 tracking-widest">
            {format(dateObj, 'MMM', { locale: es })}
          </span>
          <span className="text-xl font-bold text-white leading-none mt-1">
            {format(dateObj, 'd')}
          </span>
        </div>
      </div>

      {/* CONTENIDO INFERIOR */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform transition-transform duration-500 translate-y-2 group-hover:translate-y-0">
        
        {/* NUEVO: TAGS / GÉNEROS */}
        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
             {event.tags.slice(0, 3).map((tag, i) => (
               <span key={i} className="text-[10px] font-bold uppercase tracking-wider text-white/90 bg-white/20 backdrop-blur-sm border border-white/10 px-2 py-1 rounded-full">
                 {tag}
               </span>
             ))}
          </div>
        )}

        {/* Ubicación */}
        <div className="flex items-center gap-2 mb-2 text-white/60 text-xs font-medium uppercase tracking-wider">
           <MapPin size={12} />
           <span>{event.location || 'Secret Location'}</span>
        </div>

        <h2 className="text-2xl font-bold text-white mb-2 leading-tight line-clamp-2 group-hover:text-white transition-colors">
          {event.name}
        </h2>

        <p className="text-white/50 text-sm mb-4 line-clamp-1">
           {format(dateObj, 'EEEE, h:mm a', { locale: es })}
        </p>

        {/* Botón fantasma que aparece en hover */}
        <div className="flex items-center gap-2 text-sm font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0">
          <span>VER DETALLES</span>
          <ArrowRight size={16} />
        </div>
      </div>
    </motion.div>
  );
};

// --- COMPONENTE: SKELETON LOADING ---
const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="aspect-[3/4] rounded-3xl bg-white/5 animate-pulse border border-white/5" />
    ))}
  </div>
);

// --- COMPONENTE: PLACEHOLDER (COMING SOON) ---
const PlaceholderCard = ({ index }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.5 + (index * 0.1) }}
    className="relative w-full aspect-[3/4] rounded-3xl border border-dashed border-white/10 bg-white/[0.02] flex flex-col items-center justify-center group overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="p-4 rounded-full bg-white/5 mb-4 group-hover:scale-110 transition-transform duration-500">
      <Loader className="animate-spin-slow text-white/30" size={24} />
    </div>
    <h3 className="text-white/40 font-bold tracking-widest text-sm uppercase">Próximamente</h3>
    <p className="text-white/20 text-xs mt-2">Cooking...</p>
  </motion.div>
);

export default function Explore() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_URL}/events`);
        if (!response.ok) throw new Error('Failed to load events');
        const data = await response.json();
        setEvents(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Filtro simple solo por texto (nombre del evento)
  const filteredEvents = events.filter(e => 
    e.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#050505] text-white flex flex-col font-sans selection:bg-white selection:text-black">
      
      {/* 1. HEADER CON FONDO DIFUMINADO */}
      <div className="relative pt-32 pb-12 px-6">
        {/* Glow de fondo */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] max-w-full h-[300px] bg-white/5 blur-[120px] rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <span className="text-xs font-bold text-white/50 tracking-[0.2em] uppercase mb-4 block">
                    Descubre tu ciudad
                </span>
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white">
                        Explorar <br className="hidden md:block" />
                        <span className="text-white/40">Eventos.</span>
                    </h1>

                    {/* BARRA DE BÚSQUEDA CLEAN (Sin Filtros) */}
                    <div className="w-full md:w-auto">
                        <div className="relative group">
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-white/20 to-white/10 rounded-full blur opacity-50 group-hover:opacity-75 transition duration-500"></div>
                            <div className="relative flex items-center bg-black rounded-full px-6 py-4 w-full md:w-[350px]">
                                <Search className="text-white/40 mr-3" size={20} />
                                <input 
                                    type="text"
                                    placeholder="Buscar eventos, artistas..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="bg-transparent border-none outline-none text-white placeholder-white/30 text-sm w-full font-medium"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
      </div>

      {/* 2. GRID DE EVENTOS */}
      <div className="flex-1 w-full px-6 pb-20">
        <div className="max-w-7xl mx-auto w-full">
            
            {loading ? (
                <LoadingSkeleton />
            ) : error ? (
                <div className="text-center py-20 bg-red-500/10 rounded-3xl border border-red-500/20">
                    <p className="text-red-400">{error}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredEvents.map((event, index) => (
                        <EventCard key={event.id} event={event} index={index} />
                    ))}

                    {/* Placeholders visuales para rellenar */}
                    {[1, 2, 3].map((i) => (
                        <PlaceholderCard key={`ph-${i}`} index={i} />
                    ))}
                </div>
            )}
            
            {!loading && filteredEvents.length === 0 && events.length > 0 && (
                 <div className="text-center py-20">
                    <p className="text-white/30 text-lg">No encontramos eventos que coincidan.</p>
                 </div>
            )}

        </div>
      </div>

      <Footer />
    </div>
  );
}